import React, { useState, useEffect, useRef } from 'react';
import { FiSend, FiPaperclip, FiX, FiUsers, FiArrowLeft, FiMessageSquare } from 'react-icons/fi';
import socketService from '../../services/socketService';
import chatService from '../../services/chatService';
import './AdminChatWindow.css';

const AdminChatWindow = ({ conversation, onClose, currentAdmin }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    if (conversation?.id) {
      loadMessages();
      socketService.joinConversation(conversation.id);

      // Set up socket listeners
      socketService.onNewMessage(handleNewMessage);
      socketService.onUserTyping(handleUserTyping);
      socketService.onUserStoppedTyping(handleUserStoppedTyping);

      return () => {
        socketService.leaveConversation(conversation.id);
        socketService.removeListener('new_message');
        socketService.removeListener('user_typing');
        socketService.removeListener('user_stopped_typing');
      };
    }
  }, [conversation?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await chatService.getMessages(conversation.id);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewMessage = (message) => {
    if (message.conversation_id === conversation.id) {
      setMessages(prev => [...prev, message]);
      
      // Mark as read if not sent by current admin
      if (message.sender_admin_id !== currentAdmin?.id) {
        socketService.markAsRead({
          conversationId: conversation.id,
          messageIds: [message.id]
        });
      }
    }
  };

  const handleUserTyping = ({ conversationId, userId }) => {
    if (conversationId === conversation.id && userId !== currentAdmin?.id) {
      setTypingUsers(prev => new Set([...prev, userId]));
    }
  };

  const handleUserStoppedTyping = ({ conversationId, userId }) => {
    if (conversationId === conversation.id) {
      setTypingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    const messageData = {
      conversationId: conversation.id,
      content: newMessage.trim(),
      messageType: 'text'
    };

    socketService.sendMessage(messageData);
    setNewMessage('');
    stopTyping();
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);

    if (!typing) {
      setTyping(true);
      socketService.startTyping(conversation.id);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping();
    }, 2000);
  };

  const stopTyping = () => {
    if (typing) {
      setTyping(false);
      socketService.stopTyping(conversation.id);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const getParticipantName = (message) => {
    if (message.senderUser) {
      return `${message.senderUser.first_name} ${message.senderUser.last_name}`;
    }
    if (message.senderVendor) {
      return message.senderVendor.company_name;
    }
    if (message.senderAdmin) {
      return `${message.senderAdmin.first_name} ${message.senderAdmin.last_name} (Admin)`;
    }
    return 'Unknown';
  };

  const isOwnMessage = (message) => {
    return message.sender_admin_id === currentAdmin?.id;
  };

  const getParticipantsList = () => {
    return conversation.participants
      ?.filter(p => p.admin_user_id !== currentAdmin?.id)
      .map(p => {
        if (p.user) return `${p.user.first_name} ${p.user.last_name}`;
        if (p.vendor) return p.vendor.company_name;
        return 'Unknown';
      })
      .join(', ') || 'No other participants';
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  return (
    <div className="admin-chat-window">
      <div className="admin-chat-header">
        <button className="mobile-back-btn" onClick={onClose}>
          <FiArrowLeft />
        </button>
        <div className="admin-chat-header-info">
          <h3>{conversation.title || 'Chat'}</h3>
          <div className="admin-chat-participants">
            <FiUsers />
            <span>{getParticipantsList()}</span>
          </div>
        </div>
        <button className="admin-chat-close-btn" onClick={onClose}>
          <FiX />
        </button>
      </div>

      <div className="admin-chat-messages">
        {loading ? (
          <div className="admin-chat-loading">
            <div className="spinner"></div>
            <p>Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="admin-chat-empty">
            <FiMessageSquare size={48} />
            <p>No messages yet</p>
            <small>Start the conversation!</small>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date} className="message-date-group">
              <div className="message-date-divider">
                <span>{date}</span>
              </div>
              {dateMessages.map((message) => (
                <div
                  key={message.id}
                  className={`admin-chat-message ${isOwnMessage(message) ? 'own-message' : 'other-message'}`}
                >
                  <div className="message-content">
                    {!isOwnMessage(message) && (
                      <div className="message-sender">{getParticipantName(message)}</div>
                    )}
                    <div className="message-text">{message.content}</div>
                    <div className="message-time">{formatTime(message.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
        
        {typingUsers.size > 0 && (
          <div className="typing-indicator">
            <div className="typing-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span className="typing-text">Someone is typing...</span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <form className="admin-chat-input-form" onSubmit={handleSendMessage}>
        <button type="button" className="attach-btn" title="Attach file">
          <FiPaperclip />
        </button>
        <input
          type="text"
          className="admin-chat-input"
          placeholder="Type a message..."
          value={newMessage}
          onChange={handleTyping}
          onBlur={stopTyping}
        />
        <button type="submit" className="send-btn" disabled={!newMessage.trim()}>
          <FiSend />
        </button>
      </form>
    </div>
  );
};

export default AdminChatWindow;

